import React, { useEffect, useRef, useState } from 'react';
import {
  ViewfinderCircleIcon,
  InboxIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentListIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  FlagIcon,
  MagnifyingGlassIcon,
  GlobeAltIcon,
  BookOpenIcon,
  PaperAirplaneIcon,
  BuildingOffice2Icon,
  ChartBarSquareIcon,
  ScaleIcon,
  LinkIcon,
  ArrowsRightLeftIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { openTeamsShare, composeShareMessage } from '../lib/shareToTeams';

// ============================================================================
// PlatformLauncher — cross-app menu shared by Mandate, Scrubber, and any
// other separate-Container-App so the user has a consistent way to jump to
// every PCG module from any product. Mirrors Core's AppSidebar visually,
// but each tile is a plain <a href> (native top-level navigation) since
// the user is leaving this app.
// ============================================================================
// Mirror of Core's APP_META (src/components/AppSidebar.jsx).
const PLATFORM_APP_META = {
  overmind:         { Icon: ViewfinderCircleIcon,         label: 'PSI',              desc: 'Compliance command center',     color: '#7c3aed' },
  workstream:       { Icon: InboxIcon,                    label: 'Workstream',       desc: 'Cross-module AI follow-ups',     color: '#06b6d4' },
  calendar:         { Icon: CalendarDaysIcon,             label: 'Compliance Hub',   desc: 'Obligations & calendar',        color: '#0ea5e9' },
  risk:             { Icon: ExclamationTriangleIcon,      label: 'Risk Assessment',  desc: 'Risk scoring & register',       color: '#f59e0b' },
  coa:              { Icon: ClipboardDocumentListIcon,    label: 'COA Work Plan',    desc: 'Audit & monitoring oversight',  color: '#8b5cf6' },
  cil:              { Icon: PencilSquareIcon,             label: 'Issues Log',       desc: 'Compliance issues tracking',    color: '#f97316' },
  privacy:          { Icon: ShieldCheckIcon,              label: 'Privacy',          desc: 'Incident management',           color: '#6366f1' },
  memo:             { Icon: DocumentTextIcon,             label: 'Mandate',          desc: 'Regulatory memo analysis',      color: '#10b981' },
  cap:              { Icon: FlagIcon,                     label: 'CAP',              desc: 'Corrective action plans',       color: '#eab308' },
  scrubber:         { Icon: MagnifyingGlassIcon,          label: 'Scrubber',         desc: 'Protocol compliance check',     color: '#14b8a6' },
  network_adequacy: { Icon: GlobeAltIcon,                 label: 'Network Adequacy', desc: 'Provider network analysis',      color: '#06b6d4' },
  ppm:              { Icon: BookOpenIcon,                 label: 'Policy Management', desc: 'Enterprise policy system',       color: '#818cf8' },
  ratify:           { Icon: PaperAirplaneIcon,            label: 'SignoffHub',       desc: 'Committee · CEO · board sign-off', color: '#4f46e5' },
  fdr:              { Icon: BuildingOffice2Icon,          label: 'FDR Oversight',    desc: 'Vendor & delegate oversight',    color: '#f43f5e' },
  priq:             { Icon: ScaleIcon,                    label: 'RuleIQ',           desc: 'CMS Final Rule intelligence',   color: '#f59e0b' },
  interop:          { Icon: LinkIcon,                     label: 'Interop',          desc: 'CMS-0057 APIs & PA portal',     color: '#0891b2' },
  reporter:         { Icon: ChartBarSquareIcon,           label: 'Reporter',         desc: 'Part C/D Reporting validator',  color: '#c8323f' },
  universe_requests:{ Icon: ArrowsRightLeftIcon,          label: 'Universe Requests', desc: 'Request universes from FDRs',   color: '#c8323f' },
  sentinel:         { Icon: ShieldExclamationIcon,        label: 'Sentinel',         desc: 'Fraud (FWA) investigations',    color: '#dc2626' },
};

// Mirrors Core's PANEL_CATEGORIES (src/components/AppSidebar.jsx) — keep in sync.
const PANEL_CATEGORIES = [
  { label: 'Risk Module',       apps: ['risk', 'cil'] },
  { label: 'Audit & Monitoring', apps: ['coa'] },
  { label: 'Privacy',           apps: ['privacy'] },
  { label: 'FDR',               apps: ['fdr'] },
  { label: 'FWA',               apps: ['sentinel'] },
  { label: 'Policies',          apps: ['ppm'] },
  { label: 'Tools',             apps: ['workstream', 'calendar', 'ratify', 'memo', 'cap', 'network_adequacy', 'scrubber', 'priq', 'reporter', 'universe_requests'] },
];

const EXTERNAL_TARGETS = { memo: 'mandate', scrubber: 'scrubber', knowledge: 'knowledge' };

function platformAppHref(key) {
  const isAzure = typeof window !== 'undefined' && window.location.hostname.startsWith('az-');
  const sub = (host) => isAzure ? `az-${host}` : host;
  const externalSub = EXTERNAL_TARGETS[key];
  if (externalSub) return `https://${sub(externalSub)}.precisioncompliancegroup.com/`;
  const base = `https://${sub('core')}.precisioncompliancegroup.com`;
  if (key === 'overmind') return `${base}/`;
  return `${base}/app?app=${encodeURIComponent(key)}`;
}

// Exported so App.js can render this rail as its own first column.
export function PlatformRail({ user }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('keydown', esc); };
  }, [open]);

  const initials = user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'U';
  const firstName = user?.name?.split(' ')[0] || '';

  const isAzure = typeof window !== 'undefined' && window.location.hostname.startsWith('az-');
  const coreBase = `https://${isAzure ? 'az-core' : 'core'}.precisioncompliancegroup.com`;

  return (
    <div ref={ref} style={{
      width: 64, flexShrink: 0,
      height: '100vh', position: 'fixed', top: 0, left: 0,
      background: 'rgba(15, 23, 42, 0.95)',
      backdropFilter: 'blur(24px)',
      WebkitBackdropFilter: 'blur(24px)',
      borderRight: '1px solid rgba(255,255,255,0.08)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: '16px 0',
      zIndex: 41,
    }}>
      {/* Brand — same as Core's AppSidebar (36x36 gradient with "PCG" letters).
          Links to POM dashboard, not the platform selector. */}
      <a href={coreBase + '/app?app=overmind'} title="Back to Core"
        style={{
          width: 36, height: 36, borderRadius: 12,
          background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
          marginBottom: 16, textDecoration: 'none',
        }}
      >
        <span style={{ color: '#fff', fontSize: 9, fontWeight: 800, letterSpacing: '0.15em' }}>PCG</span>
      </a>

      {/* POM — permanent button (matches Core). Links directly to the POM
          dashboard (/app?app=overmind), not the root, which would otherwise
          dump the user on the platform selector (PCG / PMRA / NA). */}
      <a href={coreBase + '/app?app=overmind'} title="POM"
        style={{
          width: 40, height: 40, borderRadius: 12,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'transparent', textDecoration: 'none',
          marginBottom: 6, transition: 'background 0.15s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        <ViewfinderCircleIcon style={{ width: 18, height: 18, color: 'rgba(255,255,255,0.7)' }} strokeWidth={1.75} />
      </a>

      {/* Divider */}
      <div style={{ width: 24, height: 1, background: 'rgba(255,255,255,0.08)', margin: '6px 0' }} />

      {/* Apps grid icon button — opens launcher panel */}
      <button type="button" onClick={() => setOpen(o => !o)} title="Apps"
        style={{
          width: 40, height: 40, borderRadius: 12,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: open ? 'linear-gradient(145deg, rgba(59,130,246,0.20), rgba(139,92,246,0.20))' : 'transparent',
          border: open ? '1px solid rgba(59,130,246,0.30)' : '1px solid transparent',
          cursor: 'pointer', marginBottom: 12, transition: 'all 0.15s',
        }}
        onMouseEnter={(e) => { if (!open) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
        onMouseLeave={(e) => { if (!open) e.currentTarget.style.background = 'transparent'; }}
      >
        <svg width="16" height="16" fill={open ? '#fff' : 'rgba(255,255,255,0.55)'} viewBox="0 0 16 16">
          <rect x="1" y="1" width="6" height="6" rx="1.5"/>
          <rect x="9" y="1" width="6" height="6" rx="1.5"/>
          <rect x="1" y="9" width="6" height="6" rx="1.5"/>
          <rect x="9" y="9" width="6" height="6" rx="1.5"/>
        </svg>
      </button>

      {/* spacer → pin Share-to-Teams to the bottom of the rail */}
      <div style={{ flex: 1 }} />

      {/* Share to Teams — same capability as Core's rail. Opens Microsoft's
          native share dialog with a deep link back to the current view.
          Nothing routes through PSI servers; works for federated tenants too. */}
      {user && (
        <button
          type="button"
          title="Share to Teams"
          aria-label="Share to Teams"
          onClick={() => openTeamsShare({
            href: typeof window !== 'undefined' ? window.location.href : '',
            message: composeShareMessage({ appName: 'Precision Scrubber', summary: 'Scrubber review' }),
          })}
          style={{
            width: 40, height: 40, borderRadius: 12,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'transparent', border: '1px solid transparent',
            cursor: 'pointer', marginBottom: 6, transition: 'background 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="17.6" cy="6.7" r="3.1" fill="#7b83eb" />
            <rect x="2.4" y="5.6" width="13" height="13" rx="2.7" fill="#5b5fc7" />
            <rect x="5" y="8.3" width="7.8" height="1.9" rx="0.6" fill="#fff" />
            <rect x="7.95" y="8.3" width="1.9" height="7.8" rx="0.6" fill="#fff" />
          </svg>
        </button>
      )}

      {/* Apps panel — slides out to the right of the rail. Mirrors Core's
          AppSidebar layout: header / categorized grid / user footer. */}
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 30 }} />
          <div style={{
            position: 'fixed', top: 0, left: 64, height: '100vh',
            width: 320, zIndex: 40,
            background: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            borderRight: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '4px 0 24px rgba(0,0,0,0.5)',
            display: 'flex', flexDirection: 'column',
          }}>
            <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#fff' }}>
                  {firstName ? `Hi, ${firstName}` : 'Apps'}
                </h2>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>Switch workspace</p>
              </div>
              <button onClick={() => setOpen(false)} title="Close"
                style={{
                  width: 24, height: 24, borderRadius: 6,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: 'rgba(255,255,255,0.55)',
                }}
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 12px' }}>
              {PANEL_CATEGORIES.map(cat => (
                <div key={cat.label} style={{ marginBottom: 20 }}>
                  <p style={{ margin: '0 0 6px', padding: '0 10px', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.40)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    {cat.label}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {cat.apps.map(key => {
                      const meta = PLATFORM_APP_META[key];
                      if (!meta) return null;
                      return (
                        <a key={key} href={platformAppHref(key)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 12,
                            padding: '8px 8px', borderRadius: 12,
                            textDecoration: 'none',
                            background: 'transparent',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(51, 65, 85, 0.4)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <div style={{
                            width: 36, height: 36, borderRadius: 12, flexShrink: 0,
                            background: meta.color + '20',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            {meta.Icon && <meta.Icon style={{ width: 18, height: 18, color: meta.color }} strokeWidth={1.75} />}
                          </div>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <p style={{ margin: 0, fontSize: 13, fontWeight: 500, color: '#e2e8f0', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {meta.label}
                            </p>
                            {meta.desc && (
                              <p style={{ margin: 0, fontSize: 11, color: '#64748b', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {meta.desc}
                              </p>
                            )}
                          </div>
                        </a>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid rgba(96,165,250,0.3)',
              }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: '#fff' }}>{initials}</span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 11, fontWeight: 500, color: 'rgba(226,232,240,0.85)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user?.name || 'User'}
                </p>
                <p style={{ margin: 0, fontSize: 10, color: 'rgba(255,255,255,0.45)', textTransform: 'capitalize' }}>
                  {user?.role || 'user'}
                </p>
              </div>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#34d399' }} title="Online" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ScrubberSidebar({ user, onNavigate, activeView, notificationCount = 0 }) {
  const userRole = user?.role || 'stakeholder';
  const isComplianceAdmin = userRole === 'compliance_admin' || userRole === 'superuser';
  const [hoveredId, setHoveredId] = useState(null);

  const IconWrapper = ({ children, size = 20 }) => (
    <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.8 }}>
      {children}
    </div>
  );

  const menuItems = [
    {
      id: 'scrubber',
      icon: (
        <IconWrapper>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '100%', height: '100%' }}>
            <path d="m7 11 2-2-2-2"/><path d="M11 13h4"/><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/>
          </svg>
        </IconWrapper>
      ),
      label: 'Universe Scrubber',
      accessible: true
    },
    {
      id: 'submissions',
      icon: (
        <IconWrapper>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '100%', height: '100%' }}>
            <path d="M9 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-4"/>
            <path d="M9 7 L15 7"/><path d="M9 11 L15 11"/><rect width="6" height="4" x="9" y="3" rx="1"/>
          </svg>
        </IconWrapper>
      ),
      label: 'Compliance Review',
      accessible: true,
      hasNotification: notificationCount > 0,
      notificationCount: notificationCount
    },
    ...(isComplianceAdmin ? [
      {
        id: 'user-management',
        icon: (
          <IconWrapper>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '100%', height: '100%' }}>
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
              <path d="m22 21-3-3m0 0-3-3m3 3 3 3m-3-3 3-3"/>
            </svg>
          </IconWrapper>
        ),
        label: 'User Management',
        accessible: true
      },
      {
        id: 'effectiveness-metrics',
        icon: (
          <IconWrapper>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '100%', height: '100%' }}>
              <path d="M3 3v18h18"/><path d="m19 9-5 5-4-4-3 3"/>
            </svg>
          </IconWrapper>
        ),
        label: 'Effectiveness Metrics',
        accessible: true
      }
    ] : []),
    {
      id: 'reports',
      icon: (
        <IconWrapper>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '100%', height: '100%' }}>
            <rect width="18" height="18" x="3" y="4" rx="2" ry="2"/>
            <line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/>
          </svg>
        </IconWrapper>
      ),
      label: 'Reports & Analytics',
      accessible: true
    },
    {
      id: 'fdr-invitations',
      icon: (
        <IconWrapper>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '100%', height: '100%' }}>
            <path d="M3 8l9 6 9-6"/><rect width="18" height="14" x="3" y="5" rx="2" ry="2"/>
          </svg>
        </IconWrapper>
      ),
      label: 'FDR Invitations',
      accessible: true
    },
    {
      id: 'fdr-submissions',
      icon: (
        <IconWrapper>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '100%', height: '100%' }}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <path d="M14 2v6h6"/><path d="M9 13h6"/><path d="M9 17h4"/>
          </svg>
        </IconWrapper>
      ),
      label: 'FDR Submissions',
      accessible: true
    },
    {
      id: 'aggregator',
      icon: (
        <IconWrapper>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '100%', height: '100%' }}>
            <circle cx="6" cy="6" r="3"/><circle cx="18" cy="6" r="3"/><circle cx="12" cy="18" r="3"/>
            <path d="M6 9v2a4 4 0 0 0 4 4h4a4 4 0 0 0 4-4V9"/>
          </svg>
        </IconWrapper>
      ),
      label: 'Universe Aggregator',
      accessible: true
    }
  ];

  const handleNavigation = (viewId) => { if (onNavigate) onNavigate(viewId); };

  return (
    <div style={{
      width: '72px',
      background: '#0a0d14',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '0',
      borderRight: '1px solid rgba(255, 255, 255, 0.06)',
      position: 'fixed',
      top: '0',
      // Offset by the platform rail (64px) — Scrubber's app-specific sidebar
      // sits to the RIGHT of the platform rail, matching every other PCG app.
      left: '64px',
      overflow: 'hidden',
      height: '100vh',
      zIndex: 40,
      flexShrink: 0,
    }}>
      {/* Scrubber brand (the platform rail with cross-app launcher is rendered
          as a SEPARATE column to the left, matching Core / CAP / Privacy). */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '64px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
        width: '100%',
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 12,
          background: 'linear-gradient(135deg, #f43f5e, #ea580c)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(244, 63, 94, 0.3)',
        }}>
          <span style={{ color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: '0.5px' }}>S</span>
        </div>
      </div>

      {/* Divider */}
      <div style={{ width: 28, height: 1, background: 'rgba(255,255,255,0.06)', margin: '8px 0 12px' }} />

      {/* Navigation */}
      <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '0 8px' }}>
        {menuItems.map(({ id, icon, label, accessible, hasNotification, notificationCount: nc }) => {
          if (!accessible) return null;
          const active = activeView === id;
          const hovered = hoveredId === id;
          return (
            <div key={id} style={{ position: 'relative' }}>
              <button
                onClick={() => handleNavigation(id)}
                onMouseEnter={() => setHoveredId(id)}
                onMouseLeave={() => setHoveredId(null)}
                style={{
                  margin: '2px 0',
                  color: active ? '#f43f5e' : 'rgba(255,255,255,0.4)',
                  position: 'relative',
                  padding: '12px',
                  borderRadius: '12px',
                  transition: 'all 0.15s ease',
                  background: active ? 'rgba(244, 63, 94, 0.1)' : hovered ? 'rgba(255, 255, 255, 0.06)' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  outline: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
                title={label}
              >
                {icon}
                {hasNotification && nc > 0 && (
                  <div style={{
                    position: 'absolute', top: 4, right: 4,
                    minWidth: 16, height: 16, backgroundColor: '#ef4444',
                    borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 9, fontWeight: 700, color: '#fff',
                    border: '2px solid #0a0d14',
                  }}>
                    {nc > 99 ? '99+' : nc}
                  </div>
                )}
              </button>
              {hovered && (
                <div style={{
                  position: 'absolute', left: '100%', marginLeft: 12,
                  top: '50%', transform: 'translateY(-50%)',
                  padding: '8px 14px', background: '#1a1f2e',
                  color: '#fff', fontSize: 13, borderRadius: 10,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  whiteSpace: 'nowrap', zIndex: 100, pointerEvents: 'none',
                  fontWeight: 600,
                }}>
                  {label}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );
}

export default ScrubberSidebar;
